import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/src/hooks/useAuth";
import { getControlSession } from "./api";
import type { ControlMember } from "./permissions";

export type ControlAccessState =
  | { status: "loading"; member: null; retry: () => void }
  | { status: "unauthenticated"; member: null; retry: () => void }
  | { status: "email_verification_required"; member: null; retry: () => void }
  | { status: "forbidden"; member: null; retry: () => void }
  | { status: "disabled"; member: null; retry: () => void }
  | { status: "allowed"; member: ControlMember; retry: () => void }
  | { status: "error"; member: null; error: Error; retry: () => void };

type ControlAccessResult =
  | { status: "loading" | "unauthenticated" | "email_verification_required" | "forbidden" | "disabled"; member: null }
  | { status: "allowed"; member: ControlMember }
  | { status: "error"; member: null; error: Error };

export function classifyControlError(error: unknown): "forbidden" | "disabled" | "error" {
  const details = (error as { details?: { reason?: unknown } } | null)?.details;
  if (details?.reason === "disabled") return "disabled";
  const code = String((error as { code?: unknown } | null)?.code || "");
  if (code.endsWith("permission-denied") && details?.reason === "not-member") return "forbidden";
  return "error";
}

export function useControlAccess(): ControlAccessState {
  const { user, loading: authLoading } = useAuth();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<ControlAccessResult>({ status: "loading", member: null });
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    if (authLoading) {
      setResult({ status: "loading", member: null });
      return () => { active = false; };
    }
    if (!user) {
      setResult({ status: "unauthenticated", member: null });
      return () => { active = false; };
    }
    if (!user.emailVerified) {
      setResult({ status: "email_verification_required", member: null });
      return () => { active = false; };
    }
    setResult({ status: "loading", member: null });
    getControlSession().then(({ member }) => {
      if (active) setResult({ status: "allowed", member });
    }).catch((cause: unknown) => {
      if (!active) return;
      const status = classifyControlError(cause);
      if (status === "error") {
        setResult({ status, member: null, error: cause instanceof Error ? cause : new Error(String(cause)) });
      } else {
        setResult({ status, member: null });
      }
    });
    return () => { active = false; };
  }, [attempt, authLoading, user]);

  return { ...result, retry } as ControlAccessState;
}
